/**
 * PDF Storage Module
 *
 * Uses IndexedDB (via idb-keyval) to persist:
 * - PDF bytes
 * - OCR JSON sidecar data
 * - Document metadata
 * - Prompt anchors (selectors for prompt positioning)
 *
 * This enables offline access and reload of previously viewed PDFs.
 */

import { createStore, get, set, del, keys } from "idb-keyval";
import { OcrDocument } from "./ocrTypes.js";

// Create separate databases for different data types
const pdfStore = createStore("orbit-pdf-bytes", "store");
const ocrStore = createStore("orbit-pdf-ocr", "store");
const metaStore = createStore("orbit-pdf-meta", "store");
const promptAnchorStore = createStore("orbit-pdf-prompt-anchors", "store");

export interface PdfMetadata {
  docId: string;
  fileName: string;
  numPages: number;
  hasNativeText: boolean;
  hasOcr: boolean;
  uploadedAt?: number;
  createdAt?: number;
  lastOpenedAt?: number;
}

/**
 * Selector for anchoring a prompt to a specific text position.
 */
export interface PdfTextPositionSelector {
  type: "PdfTextPositionSelector";
  pageIndex: number;
  start: number;
  end: number;
  exact: string;
}

/**
 * Anchor data stored for a prompt.
 */
export interface PromptAnchorData {
  selector: PdfTextPositionSelector;
  question: string;
  answer: string;
  createdAt: number;
}

/**
 * All anchors for a document, keyed by task entity ID.
 */
export type DocPromptAnchors = Record<string, PromptAnchorData>;

/**
 * Generates a stable document ID from PDF bytes using SHA-256
 */
export async function generateDocId(pdfBytes: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest("SHA-256", pdfBytes);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // Use base64url encoding for URL-safe IDs
  const base64 = btoa(String.fromCharCode(...hashArray));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// --- PDF Bytes ---

export async function savePdf(
  docId: string,
  meta: Omit<PdfMetadata, "createdAt" | "lastOpenedAt"> & { uploadedAt?: number },
  bytes: ArrayBuffer
): Promise<void> {
  const now = Date.now();
  const fullMeta: PdfMetadata = {
    ...meta,
    createdAt: meta.uploadedAt || now,
    lastOpenedAt: now,
    uploadedAt: meta.uploadedAt || now,
  };
  await Promise.all([
    set(docId, bytes, pdfStore),
    set(docId, fullMeta, metaStore),
  ]);
}

export async function loadPdf(docId: string): Promise<ArrayBuffer | undefined> {
  const bytes = await get<ArrayBuffer>(docId, pdfStore);
  if (bytes) {
    // Update last opened timestamp
    const meta = await get<PdfMetadata>(docId, metaStore);
    if (meta) {
      meta.lastOpenedAt = Date.now();
      await set(docId, meta, metaStore);
    }
  }
  return bytes;
}

export async function getPdfMetadata(docId: string): Promise<PdfMetadata | undefined> {
  return get<PdfMetadata>(docId, metaStore);
}

export async function listAllPdfs(): Promise<PdfMetadata[]> {
  const allKeys = await keys(metaStore);
  const metas = await Promise.all(
    allKeys.map((key) => get<PdfMetadata>(key as string, metaStore))
  );
  // Sort by most recently opened
  return metas
    .filter((m): m is PdfMetadata => m !== undefined)
    .sort((a, b) => (b.lastOpenedAt || 0) - (a.lastOpenedAt || 0));
}

export async function deletePdf(docId: string): Promise<void> {
  await Promise.all([
    del(docId, pdfStore),
    del(docId, ocrStore),
    del(docId, metaStore),
    del(docId, promptAnchorStore),
  ]);
}

// --- OCR Data ---

export async function saveOcr(docId: string, ocrDoc: OcrDocument): Promise<void> {
  await set(docId, ocrDoc, ocrStore);
  // Update metadata
  const meta = await get<PdfMetadata>(docId, metaStore);
  if (meta) {
    meta.hasOcr = true;
    await set(docId, meta, metaStore);
  }
}

export async function loadOcr(docId: string): Promise<OcrDocument | undefined> {
  return get<OcrDocument>(docId, ocrStore);
}

// --- Prompt Anchors ---

/**
 * Save a prompt anchor for a specific task entity ID.
 */
export async function savePdfPromptAnchor(
  docId: string,
  taskEntityId: string,
  anchor: PromptAnchorData
): Promise<void> {
  const existing = await get<DocPromptAnchors>(docId, promptAnchorStore) || {};
  existing[taskEntityId] = anchor;
  await set(docId, existing, promptAnchorStore);
}

/**
 * Load all prompt anchors for a document.
 */
export async function loadPdfPromptAnchors(
  docId: string
): Promise<DocPromptAnchors> {
  return (await get<DocPromptAnchors>(docId, promptAnchorStore)) || {};
}

/**
 * Delete a specific prompt anchor.
 */
export async function deletePdfPromptAnchor(
  docId: string,
  taskEntityId: string
): Promise<void> {
  const existing = await get<DocPromptAnchors>(docId, promptAnchorStore);
  if (existing && existing[taskEntityId]) {
    delete existing[taskEntityId];
    await set(docId, existing, promptAnchorStore);
  }
}

/**
 * Clear all prompt anchors for a document.
 */
export async function clearPdfPromptAnchors(docId: string): Promise<void> {
  await del(docId, promptAnchorStore);
}

// --- Utility ---

export async function hasPdf(docId: string): Promise<boolean> {
  const meta = await get<PdfMetadata>(docId, metaStore);
  return meta !== undefined;
}
